// Utility API per la gestione degli scambi di carte Magic
// Tutte le chiamate vengono eseguite tramite API routes lato server

/**
 * Recupera tutti gli scambi disponibili
 */
export const fetchScambi = async (page = 1, pageSize = 50, filters = {}) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    // Aggiungi i filtri se presenti
    if (filters.tipo) {
      params.append('tipo', filters.tipo);
    }

    if (filters.stato && Array.isArray(filters.stato)) {
      filters.stato.forEach((stato) => params.append('stato', stato));
    } else if (filters.stato) {
      params.append('stato', filters.stato);
    }

    if (filters.userId) {
      params.append('userId', filters.userId);
    }

    if (filters.nome) {
      params.append('nome', filters.nome);
    }

    if (filters.username) {
      params.append('username', filters.username);
    }

    const response = await fetch(`/api/scambi-carte?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Errore nel recupero degli scambi');
    }

    const data = await response.json();
    return {
      scambi: data.scambi || [],
      pagination: data.pagination || {},
      statistics: data.statistics || {},
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel fetch degli scambi:', error);
    throw error;
  }
};

/**
 * Recupera gli scambi dell'utente autenticato
 */
export const fetchScambiByUser = async (
  tipo = null,
  page = 1,
  pageSize = 50,
  filters = {}
) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    if (tipo) {
      params.append('tipo', tipo);
    }

    // Aggiungi i filtri aggiuntivi se presenti
    if (filters.stato && Array.isArray(filters.stato)) {
      filters.stato.forEach((stato) => params.append('stato', stato));
    } else if (filters.stato) {
      params.append('stato', filters.stato);
    }

    if (filters.userId) {
      params.append('userId', filters.userId);
    }

    if (filters.nome) {
      params.append('nome', filters.nome);
    }

    if (filters.username) {
      params.append('username', filters.username);
    }

    const response = await fetch(`/api/scambi-carte/user?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Errore nel recupero degli scambi utente');
    }

    const data = await response.json();
    return {
      scambi: data.scambi || [],
      pagination: data.pagination || {},
      statistics: data.statistics || {},
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel fetch degli scambi utente:', error);
    throw error;
  }
};

/**
 * Crea un nuovo scambio
 */
export const createScambio = async (scambioData) => {
  try {
    const response = await fetch('/api/scambi-carte', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(scambioData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || 'Errore nella creazione dello scambio'
      );
    }

    const data = await response.json();
    return data.scambio;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella creazione dello scambio:', error);
    throw error;
  }
};

/**
 * Aggiorna uno scambio esistente
 */
export const updateScambio = async (documentId, scambioData) => {
  try {
    const response = await fetch(`/api/scambi-carte/${documentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(scambioData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Errore nell'aggiornamento dello scambio"
      );
    }

    const data = await response.json();
    return data.scambio;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'aggiornamento dello scambio:", error);
    throw error;
  }
};

/**
 * Aggiorna lo stato di uno scambio
 */
export const updateScambioStatus = async (documentId, newStatus) => {
  try {
    const response = await fetch(`/api/scambi-carte/${documentId}/status`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ status: newStatus }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Errore nell'aggiornamento dello stato"
      );
    }

    const data = await response.json();
    return data.scambio;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'aggiornamento dello stato:", error);
    throw error;
  }
};

/**
 * Rimuove uno scambio
 */
export const removeScambio = async (documentId) => {
  try {
    const response = await fetch(`/api/scambi-carte/${documentId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || 'Errore nella rimozione dello scambio'
      );
    }

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella rimozione dello scambio:', error);
    throw error;
  }
};

/**
 * Cerca carte tramite Scryfall API (chiamata lato server)
 */
export const searchScryfall = async (query) => {
  try {
    const response = await fetch(
      `/api/scambi-carte/search?query=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Errore nella ricerca Scryfall');
    }

    const data = await response.json();
    return data.cards || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella ricerca Scryfall:', error);
    throw error;
  }
};

/**
 * Cerca carte tramite Scryfall API con parametri avanzati
 */
export const searchScryfallAdvanced = async (
  cardName,
  setCode = null,
  collectorNumber = null
) => {
  try {
    let query = cardName;

    // Se abbiamo set code e collector number, costruisci query avanzata
    if (setCode && collectorNumber) {
      query = `name:"${cardName}" set:${setCode} number:${collectorNumber}`;
    } else if (setCode) {
      query = `name:"${cardName}" set:${setCode}`;
    }

    const response = await fetch(
      `/api/scambi-carte/search?query=${encodeURIComponent(query)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Errore nella ricerca Scryfall avanzata');
    }

    const data = await response.json();
    return data.cards || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella ricerca Scryfall avanzata:', error);
    throw error;
  }
};

/**
 * Recupera i set disponibili da Scryfall API (chiamata lato server)
 */
export const fetchScryfallSets = async () => {
  try {
    const response = await fetch('/api/scambi-carte/sets', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Errore nel recupero dei set Scryfall');
    }

    const data = await response.json();
    return data.sets || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel recupero dei set Scryfall:', error);
    throw error;
  }
};

/**
 * Recupera tutti i set disponibili per una carta specifica
 */
export const fetchCardSets = async (cardName) => {
  try {
    const response = await fetch(
      `/api/scambi-carte/card-sets/${encodeURIComponent(cardName)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Errore nel recupero dei set per la carta');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel recupero dei set per la carta:', error);
    throw error;
  }
};

/**
 * Filtra gli scambi per nome carta o utente
 */
export const filterScambi = async (filters, page = 1, pageSize = 50) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params.append(key, filters[key]);
      }
    });

    const response = await fetch(`/api/scambi-carte/filter?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Errore nel filtraggio degli scambi');
    }

    const data = await response.json();
    return {
      scambi: data.scambi || [],
      pagination: data.pagination || {},
      statistics: data.statistics || {},
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nel filtraggio degli scambi:', error);
    throw error;
  }
};

/**
 * Indica che l'utente ha una carta che qualcuno cerca
 */
export const indicateCardAvailable = async (documentId) => {
  try {
    // Recuperiamo i dati completi dell'utente corrente usando /api/users/me
    const userResponse = await fetch('/api/users/me', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!userResponse.ok) {
      throw new Error('Errore nel recupero dei dati utente');
    }

    const userData = await userResponse.json();

    // Creiamo il tag con i dati dell'utente
    const telefono =
      userData.tesseramenti && userData.tesseramenti.length > 0
        ? userData.tesseramenti[0].telefono
        : 'no-tel';
    const userTag = `ce-lho_${userData.firstName}_${userData.lastName}_${telefono}`;

    const response = await fetch(`/api/scambi-carte/${documentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        tags: [userTag], // Salviamo il tag invece dei contatti
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Errore nell'aggiunta del tag");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'aggiunta del tag:", error);
    throw error;
  }
};

/**
 * Indica che l'utente vuole una carta che qualcuno offre
 */
export const indicateCardWanted = async (documentId) => {
  try {
    // Recuperiamo i dati completi dell'utente corrente usando /api/users/me
    const userResponse = await fetch('/api/users/me', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!userResponse.ok) {
      throw new Error('Errore nel recupero dei dati utente');
    }

    const userData = await userResponse.json();

    // Creiamo il tag con i dati dell'utente
    const telefono =
      userData.tesseramenti && userData.tesseramenti.length > 0
        ? userData.tesseramenti[0].telefono
        : 'no-tel';
    const userTag = `mi-serve_${userData.firstName}_${userData.lastName}_${telefono}`;

    const response = await fetch(`/api/scambi-carte/${documentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        tags: [userTag], // Salviamo il tag invece dei contatti
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Errore nell'aggiunta del tag");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Errore nell'aggiunta del tag:", error);
    throw error;
  }
};

/**
 * Rimuove l'indicazione di disponibilità o interesse
 */
export const removeCardIndication = async (documentId) => {
  try {
    // Per ora rimuoviamo tutti i tags impostando un array vuoto
    // In futuro potremmo implementare una logica più sofisticata per rimuovere solo i tag dell'utente specifico
    const response = await fetch(`/api/scambi-carte/${documentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        tags: [], // Rimuoviamo tutti i tags per ora
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Errore nella rimozione del tag');
    }

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella rimozione del tag:', error);
    throw error;
  }
};

/**
 * Rimuove un contatto da uno scambio
 */
export const removeContatto = async (documentId) => {
  try {
    // Per ora rimuoviamo tutti i tags impostando un array vuoto
    // In futuro potremmo implementare una logica più sofisticata per rimuovere solo i tag dell'utente specifico
    const response = await fetch(`/api/scambi-carte/${documentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        tags: [], // Rimuoviamo tutti i tags per ora
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Errore nella rimozione del tag');
    }

    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Errore nella rimozione del tag:', error);
    throw error;
  }
};
